import { ReactElement } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Icon,
  Text,
} from "@chakra-ui/react";
import { IoAlertCircleOutline, IoRefresh } from "react-icons/io5";
import { useModifiedTranslation } from "../../hooks/useModifiedTranslation";
import { isApiError } from "../../utils/apiClients/ApiError";

// 読み込みに失敗したときの帯。message で何を読めなかったかを伝え、「もう一度」で onRetry を呼ぶ。
// 帯を閉じると再試行の手段が無くなるので、閉じるボタンは置かない。
// API が返した HTTP の番号をエラー番号として出す。電波が無いときなど番号が無ければ出さない。
// 地は Chakra の既定の赤のままにして、ダークテーマで明るく浮かないようにする。
// _dark を渡すとテーマ側のダーク用の地と文字の色を丸ごと上書きするので、縁の色は両方のテーマで同じにする
export function LoadErrorBand(
  props: Readonly<{
    error: unknown;
    message: string;
    onRetry: () => void;
    testId: string;
    retryTestId: string;
  }>,
): ReactElement {
  const { error, message, onRetry, testId, retryTestId } = props;
  const t = useModifiedTranslation();
  const status = isApiError(error) ? error.status : null;

  return (
    <Alert
      status="error"
      rounded="md"
      alignItems="flex-start"
      borderWidth="1px"
      borderColor="red.200"
      my={3}
      data-testid={testId}
    >
      <AlertIcon>
        <Icon as={IoAlertCircleOutline} boxSize="full" />
      </AlertIcon>
      <Box flex={1}>
        {/* 既定ではインラインで、「もう一度」が文の続きに並ぶので、ブロックにして次の行へ送る */}
        <AlertDescription display="block">{message}</AlertDescription>
        <Button
          size="sm"
          variant="link"
          colorScheme="red"
          fontWeight="bold"
          leftIcon={<IoRefresh />}
          mt={2}
          onClick={onRetry}
          data-testid={retryTestId}
        >
          {t("LoadErrorBand.retry")}
        </Button>
        {status !== null && (
          <Text fontSize="xs" fontWeight="normal" mt={1}>
            {t("LoadErrorBand.number", { status })}
          </Text>
        )}
      </Box>
    </Alert>
  );
}
